<script lang="ts">
  import type { Snippet } from 'svelte';
  import { parseTs, timeAgo } from '../admin';

  /**
   * The heading every admin page opens with.
   *
   * WHAT IT FIXES. Eighteen pages had each written their own, and they had drifted on
   * every axis at once: three title sizes (`text-xl`/`text-2xl`/`text-3xl`), five
   * competing eyebrow vocabularies ("Mission Control · Pipelines", "Sideguide AI ·
   * Settings", "Sideguide · Superadmin", "Superadmin", "Insights · Contributors"), and
   * a freshness readout on four pages out of eighteen. Six of them printed the page's
   * own name twice — "Mission Control · Pipelines" above a title that said "Pipelines".
   *
   * THE RULE THAT MAKES THAT UNREPRESENTABLE. `section` is the SIDEBAR SECTION the page
   * lives in and `title` is the page. A section is never a leaf, so the eyebrow can no
   * longer restate the title — the duplication was not a series of typos, it was what
   * happens when "what goes in the eyebrow?" has no answer. Now it has one, and it is
   * the same taxonomy the navigation already teaches (`AdminSidebar`'s tree), so the
   * header tells you where you are in the terms the sidebar just used.
   *
   * WHAT IT ADDS. Freshness, everywhere rather than on four pages. In a console whose
   * every number is computed by a pipeline that runs on a timer, "when was this last
   * true?" is the one question the page cannot answer by showing more numbers — and it
   * is the question a stale board silently gets wrong. It reads as an instrument's
   * last-reading stamp: a live dot and a mono age, aligned to the right end of the
   * title's baseline rule.
   *
   * `scope` is the other half of that: the window or filter that governs the WHOLE page
   * ("Last 12 months · UTC", "Excludes moderators"), which pages used to bury in one
   * card's eyebrow and leave the reader to generalise from. It is not a summary of the
   * data — that is what the deleted stat sentences were, and they are not coming back.
   *
   * THE SIGNATURE is the hairline the title sits on: the section rides above its left
   * end, the freshness stamp sits on its right. It is the app's own `.hair` + `.eyebrow`
   * texture promoted from decoration to structure, and it is what makes eighteen
   * different pages read as one instrument.
   */
  let {
    section,
    title,
    updated = null,
    updatedLabel = 'Data last refreshed',
    scope = null,
    actions = null,
    titleExtra = null,
  }: {
    /** The sidebar SECTION this page sits under — never the page's own name. */
    section: string;
    title: string;
    /** UTC-aware instant the page's data was computed. Omit where the page has none;
     *  a header with no answer is better than a made-up one. */
    updated?: string | null | undefined;
    /** Tooltip prefix, for pages where the stamp means something more specific than
     *  "the data" (Usage stamps the last conversation, not a pipeline run). */
    updatedLabel?: string;
    /** The window or filter governing the whole page. One short clause, no metrics. */
    scope?: string | null | undefined;
    /** Right-aligned controls on the title's baseline. */
    actions?: Snippet | null;
    /** Adornments that belong WITH the title (a count, a state pill), inline after it. */
    titleExtra?: Snippet | null;
  } = $props();

  // The ticker lives here, once, instead of in each page that wanted a live age. Nine
  // pages had hand-rolled `nowMs` + a 30s interval; the ones that kept theirs after this
  // did so because they age ROWS as well, which is not this component's business.
  let nowMs = $state(Date.now());
  $effect(() => {
    const id = setInterval(() => (nowMs = Date.now()), 30_000);
    return () => clearInterval(id);
  });
</script>

<header class="mb-5">
  <!-- `items-end` + the shared bottom rule is the device: whatever the title's length or
       the actions' height, both sit on one line and the eye reads across it. -->
  <div class="flex flex-wrap items-end justify-between gap-x-4 gap-y-2 border-b hair pb-2.5">
    <div class="min-w-0">
      <div class="eyebrow">{section}</div>
      <h1 class="mt-1 flex flex-wrap items-baseline gap-x-2.5 font-display text-2xl font-bold tracking-tight sm:text-3xl">
        <span class="min-w-0">{title}</span>
        {#if titleExtra}{@render titleExtra()}{/if}
      </h1>
    </div>

    {#if updated || actions}
      <div class="flex shrink-0 items-center gap-3 pb-1">
        {#if updated}
          <!-- The live dot is `success` and not the theme's one blue signal on purpose:
               blue means "actionable" everywhere else here, and freshness is a state to
               read, not a thing to click. -->
          <span
            class="flex items-center gap-1.5 font-mono text-[0.7rem] text-base-content/65"
            title={`${updatedLabel} ${parseTs(updated).toLocaleString()}`}
          >
            <span class="inline-block h-1.5 w-1.5 rounded-full bg-success/70"></span>
            Updated {timeAgo(updated, nowMs)}
          </span>
        {/if}
        {#if actions}{@render actions()}{/if}
      </div>
    {/if}
  </div>

  {#if scope}
    <p class="mt-2 font-mono text-[0.7rem] leading-relaxed text-base-content/55">{scope}</p>
  {/if}
</header>
